import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isNonNaNNumber } from '@iotv/iotv-v3-types';
import { useGridApiContext } from '@mui/x-data-grid';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import React, { useContext } from 'react';
import { TextField } from '@mui/material';
import { MenuItem, Select, Stack } from '@mui/material';
import { stringDuckType } from '@iotv/core-services';
import { AppMainViewContext } from '../../ContextProviders/AppContextProvider';
import { roundDecimals } from '@iotv/datamodel-core';
const debug = process.env.REACT_APP_DEBUG && false;
export const dateTimeFormatter = (timestampMs) => {
    // if today format  "Today, 1:14 pm" 
    // if yesterday format "Yesterday, 1:14 pm"
    // else format "D MMM, h:mm a"
    // except if not this year then format "D MMM YYYY, h:mm a"
    if (timestampMs && isNonNaNNumber(timestampMs)) {
        const dayjsObj = dayjs(timestampMs);
        if (dayjsObj.isSame(dayjs(), 'day')) {
            return `Today, ${dayjsObj.format('h:mm a')}`;
        }
        else if (dayjsObj.isSame(dayjs().subtract(1, 'day'), 'day')) {
            return `Yesterday, ${dayjsObj.format('h:mm a')}`;
        }
        else if (dayjsObj.isSame(dayjs(), 'year')) {
            return dayjsObj.format('D MMM, h:mm a');
        }
        else {
            return dayjsObj.format('D MMM YYYY, h:mm a');
        }
    }
    return null;
};
export const dateTimeFormatterCompact = (timestampMs) => {
    // if today format  "1:14 pm" 
    // else format "31 12 13:12"
    // except if not this year then format "31 12 23 13:12"
    if (timestampMs && isNonNaNNumber(timestampMs)) {
        const dayjsObj = dayjs(timestampMs);
        if (dayjsObj.isSame(dayjs(), 'day')) {
            return dayjsObj.format('h:mm a');
        }
        else if (dayjsObj.isSame(dayjs(), 'year')) {
            // e.g. 31 12 13:12
            return dayjsObj.format('DD MM HH:mm');
        }
        else {
            // e.g. 31 12 23 13:12
            return dayjsObj.format('DD MM YY HH:mm');
        }
    }
    return null;
};
// Edit component that receives apiRef as a prop
// Edit renderer: receives apiRef as prop
const DateColRendererEditComponent = (params) => {
    const { id, value, field, apiRef } = params;
    const [{ isSmallView }] = useContext(AppMainViewContext);
    const handleChange = (newValue) => {
        const timestamp = newValue && dayjs.isDayjs(newValue) ? newValue.valueOf() : null;
        apiRef.current.setEditCellValue({ id, field, value: timestamp });
    };
    return (_jsx(DateTimePicker, { autoFocus: true, value: value ? dayjs(value) : null, onChange: handleChange, ampm: true, format: isSmallView ? "h:mm a" : "D MMM, h:mm a",
        slotProps: isSmallView ? undefined : {
            textField: {
                fullWidth: true,
                variant: 'standard',
                size: 'small',
                sx: {
                    minWidth: 120,
                    maxWidth: 160,
                    fontSize: '0.95rem',
                    padding: '2px 6px',
                    '& .MuiInput-underline:before': { borderBottom: 'none' },
                    '& .MuiInput-underline:after': { borderBottom: 'none' },
                    '& .MuiInputBase-root:before': { borderBottom: 'none' },
                    '& .MuiInputBase-root:after': { borderBottom: 'none' },
                },
                inputProps: {
                    style: {
                        textAlign: 'center',
                        fontSize: '0.95rem',
                    },
                },
            },
        } }));
};
// Wrapper for view mode: calls useGridApiContext and passes apiRef
export const DateTimeColRendererWithApi = (params) => {
    const apiRef = useGridApiContext();
    return _jsx(DateTimeColRenderer, { ...params, apiRef: apiRef });
};
// View renderer: receives apiRef as prop
const DateTimeColRenderer = ({ value }) => {
    const [{ isSmallView }] = useContext(AppMainViewContext);
    const dataFormatFn = isSmallView ? dateTimeFormatterCompact : dateTimeFormatter;
    return isNonNaNNumber(value) ? dataFormatFn(value) : null;
};
// Wrapper for edit mode: calls useGridApiContext and passes apiRef
export const DateColRendererEditWithApi = (params) => {
    const apiRef = useGridApiContext();
    return _jsx(DateColRendererEditComponent, { ...params, apiRef: apiRef });
};
const DefaultCellRenderer = ({ value }) => {
    if (value !== null && typeof value === 'object') {
        console.log('Asked to render object value in DefaultCellRenderer, which may not display correctly:', value);
        return JSON.stringify(value);
    }
    else if (isNonNaNNumber(value)) {
        return roundDecimals(value, 2);
    }
    return value ?? null;
};
export const DefaultCellRendererWithApi = (params) => {
    const apiRef = useGridApiContext();
    return _jsx(DefaultCellRenderer, { ...params, apiRef: apiRef });
};
const DefaultCellEditRenderer = (params) => {
    const { id, value, field, apiRef } = params;
    const handleChange = (e) => {
        apiRef.current.setEditCellValue({ id: params.id, field: params.field, value: stringDuckType(e.target.value) });
    };
    return (_jsx(TextField, { autoFocus: true, value: value ?? '', onChange: handleChange, variant: "standard", fullWidth: true, InputProps: {
            sx: {
                fontSize: '0.95rem',
                padding: '2px 6px',
                '& .MuiInput-underline:before': { borderBottom: 'none' },
                '& .MuiInput-underline:after': { borderBottom: 'none' },
                '& .MuiInputBase-root:before': { borderBottom: 'none' },
                '& .MuiInputBase-root:after': { borderBottom: 'none' },
            },
            inputProps: {
                style: {
                    textAlign: 'center',
                    fontSize: '0.95rem',
                },
            },
        } }));
};
export const DefaultCellEditRendererWithApi = (params) => {
    const apiRef = useGridApiContext();
    return _jsx(DefaultCellEditRenderer, { ...params, apiRef: apiRef });
};
const PERIOD_UNITS = [
    { label: 'min', ms: 60000 },
    { label: 'hr', ms: 3600000 },
    { label: 'day', ms: 86400000 },
];
export const getBestUnit = (ms) => {
    let periodUnits = PERIOD_UNITS[0]; // default to 'min'
    if (isNonNaNNumber(ms)) {
        for (let i = PERIOD_UNITS.length - 1; i >= 0; i--) {
            if (ms % PERIOD_UNITS[i].ms === 0 && ms / PERIOD_UNITS[i].ms >= 1) {
                periodUnits = PERIOD_UNITS[i];
                break;
            }
        }
    }
    return periodUnits;
};
export const durationValueGetter = (a, row, c) => {
    const from = row.fromTimestampMs;
    const to = row.toTimestampMs;
    if (isNonNaNNumber(from) && isNonNaNNumber(to)) {
        const durationMs = to - from;
        const unit = getBestUnit(durationMs);
        const value = durationMs / unit.ms;
        // Show as integer if possible, else 2 decimals
        const displayValue = Number.isInteger(value) ? value : value.toFixed(2);
        return `${displayValue} ${unit.label}`;
    }
    return 'N/A';
};
export const interRowDurationValueGetter = (a, row, c, apiRef) => {
    const from = row.timestamp;
    // get next row's fromTimestam
    const to = apiRef?.current?.getRow(row.id + 1)?.timestamp ?? null;
    if (isNonNaNNumber(from) && isNonNaNNumber(to)) {
        const durationMs = to - from;
        const unit = getBestUnit(durationMs);
        const value = durationMs / unit.ms;
        // Show as integer if possible, else 2 decimals
        const displayValue = Number.isInteger(value) ? value : value.toFixed(2);
        return `${displayValue} ${unit.label}`;
    }
    return 'N/A';
};
export const PeriodBasisInput = (params) => {
    const { value, setValue } = params;
    // Determine best unit for display
    const initialUnit = getBestUnit(value);
    const [unit, setUnit] = React.useState(initialUnit);
    const [inputValue, setInputValue] = React.useState(() => value && typeof value === 'number' ? value / unit.ms : '');
    // When value changes externally, update local state
    React.useEffect(() => {
        setUnit(getBestUnit(value));
        setInputValue(value && typeof value === 'number' ? value / getBestUnit(value).ms : '');
    }, [value]);
    const handleValueChange = (e) => {
        const newVal = e.target.value;
        setInputValue(newVal);
        const ms = Number(newVal) * unit.ms;
        if (!isNaN(ms)) {
            setValue(ms);
        }
    };
    const handleUnitChange = (e) => {
        const newUnit = PERIOD_UNITS.find(u => u.label === e.target.value) || PERIOD_UNITS[0];
        setUnit(newUnit);
        // Keep the displayed value the same, but update setValue to newUnit * displayed value
        const displayedValue = Number(inputValue);
        const ms = displayedValue * newUnit.ms;
        if (!isNaN(ms)) {
            setValue(ms);
        }
    };
    return (_jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", children: [_jsx(TextField, { autoFocus: true, type: "number", value: inputValue, onChange: handleValueChange, variant: "outlined", size: "small", sx: { width: 80 }, inputProps: { min: 0 } }), _jsx(Select, { value: unit.label, onChange: handleUnitChange, variant: "outlined", size: "small", sx: { width: 70 }, children: PERIOD_UNITS.map(u => (_jsx(MenuItem, { value: u.label, children: u.label }, u.label))) })] }));
};
